import { type DayFormat, addDays, todayIso, useClasses, useSetDayFormat } from '@shkola/api';
import { wartimeSyncOptions } from '@shkola/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GRAD, INK, INK_FAINT, INK_SOFT, PAPER, PillIcon, grad, pillClass } from '../design/forms';
import { CheckIcon } from '../design/icons';
import { Badge, Panel, Segmented } from './ui';

/*
 * «Контрольний час» in «Форми 2» (28.10): the day and the format as the soft choices, the classes as
 * chips of the paper (as the filters of «Домашні»), the norms of the online day in a note of the paper,
 * the main button a dark pill.
 */

const FORMATS: DayFormat[] = ['online', 'shelter', 'in_person'];

/** МОЗ №1371 options of the selected grades, by group: «5–6 кл.: 2×45, 3×35, 4×25 хв». */
function useNorms(grades: number[]) {
  const { t } = useTranslation();
  const groups = new Map<string, number[]>();
  for (const grade of [...new Set(grades)].sort((a, b) => a - b)) {
    const key = wartimeSyncOptions(grade)
      .map((o) => `${o.lessons}×${o.minutes}`)
      .join(', ');
    groups.set(key, [...(groups.get(key) ?? []), grade]);
  }
  return [...groups].map(([options, list]) => {
    const from = list[0] as number;
    const to = list[list.length - 1] as number;
    const who = from === to ? `${from} кл.` : t('controlHour.normGrades', { from, to });
    return `${who}: ${options} хв`;
  });
}

const chip = (on: boolean) =>
  `inline-flex h-9 shrink-0 items-center rounded-full px-3.5 text-[13.5px] transition-colors ${on ? 'font-medium' : 'hover:bg-[#EFECE5]'}`;

/** «Контрольний час»: the administration switches classes for a day in one go. */
export function ControlHour({ schoolId }: { schoolId: string }) {
  const { t } = useTranslation();
  const classes = useClasses(schoolId);
  const apply = useSetDayFormat(schoolId);
  const today = todayIso();
  const [day, setDay] = useState<'today' | 'tomorrow'>('today');
  const [format, setFormat] = useState<DayFormat>('online');
  const [chosen, setChosen] = useState<string[]>([]);
  const [result, setResult] = useState<number>();
  const all = classes.data ?? [];
  const selected = chosen.length === 0 ? all : all.filter((c) => chosen.includes(c.id));
  const norms = useNorms(selected.map((c) => c.grade));

  return (
    <Panel title={t('controlHour.title')}>
      <p className="mb-4 text-[14px] leading-5" style={{ color: INK_SOFT }}>
        {t('controlHour.hint')}
      </p>
      <div className="flex flex-wrap gap-2">
        <Segmented
          value={day}
          options={[
            { value: 'today', label: t('controlHour.today') },
            { value: 'tomorrow', label: t('controlHour.tomorrow') },
          ]}
          onChange={setDay}
        />
        <Segmented
          value={format}
          options={FORMATS.map((f) => ({ value: f, label: t(`controlHour.formats.${f}`) }))}
          onChange={setFormat}
        />
      </div>
      <p className="mt-5 mb-2 text-[13px] font-medium" style={{ color: INK_SOFT }}>
        {t('controlHour.classes')}
      </p>
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          aria-pressed={chosen.length === 0}
          onClick={() => setChosen([])}
          className={chip(chosen.length === 0)}
          style={{ backgroundColor: chosen.length === 0 ? '#E9E5DC' : PAPER, color: INK }}
        >
          {t('controlHour.wholeSchool')}
        </button>
        {all.map((c) => {
          const on = chosen.includes(c.id);
          return (
            <button
              key={c.id}
              type="button"
              aria-pressed={on}
              onClick={() => setChosen(on ? chosen.filter((x) => x !== c.id) : [...chosen, c.id])}
              className={chip(on)}
              style={{ backgroundColor: on ? '#E9E5DC' : PAPER, color: INK }}
            >
              {c.label}
            </button>
          );
        })}
      </div>
      {format === 'online' && norms.length > 0 && (
        <div
          className="mt-4 rounded-[16px] px-3.5 py-3 text-[13px] leading-5"
          style={{ backgroundImage: grad('lavender'), color: INK }}
        >
          <p className="font-medium" style={{ color: GRAD.lavender.ink }}>
            {t('controlHour.norms')}
          </p>
          <p style={{ color: INK_SOFT }}>{norms.join(' · ')}</p>
        </div>
      )}
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={apply.isPending || all.length === 0}
          onClick={() => {
            setResult(undefined);
            apply.mutate(
              {
                classIds: chosen.length === 0 ? null : chosen,
                date: day === 'today' ? today : addDays(today, 1),
                format,
              },
              { onSuccess: (n) => setResult(n) },
            );
          }}
          className={`${pillClass('dark')} disabled:opacity-40`}
        >
          {t('controlHour.apply')}
          <PillIcon icon={CheckIcon} />
        </button>
        {result !== undefined && (
          <Badge tone={result > 0 ? 'success' : 'neutral'}>
            {result > 0 ? t('controlHour.done', { count: result }) : t('controlHour.nothing')}
          </Badge>
        )}
        {apply.isPending && (
          <span className="text-[13px]" style={{ color: INK_FAINT }}>
            …
          </span>
        )}
      </div>
    </Panel>
  );
}
